/**
 * Leaderboard API — B-2026-0072
 *
 * Covers:
 *   - getChannelLeaderboard: GET /channels/{slug}/leaderboard
 *   - toggleLeaderboardOptIn: PATCH /channels/{slug}/members/{uid}/leaderboard-opt-in
 *   - toggleChannelLeaderboard: PATCH /channels/{slug}/leaderboard
 */

import { apiClient } from '@/services/api';
import type {
  LeaderboardBoardSizeMode,
  LeaderboardRankedBy,
  LeaderboardSmallBoardBadge,
} from '@/types/goalScore';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LeaderboardEntry {
  /**
   * 1-based rank. Goal Score PR-6: `null` on a small board
   * (`board_size_mode === 'small_board'`, fewer than the small-board floor
   * ranked athletes) — read `badge` instead in that case. Never `null` on a
   * `'ranked'` board.
   */
  rank: number | null;
  user_id: string;
  display_name: string | null;
  /** Meaning depends on the response's `ranked_by`: `total_score` under `'total'`, `overall_score` under `'form'` (unchanged pre-PR-6 semantics). */
  personal_best: number;
  session_count: number;
  last_eval_at: string | null;
  is_current_user: boolean;
  /**
   * Goal Score PR-6: a qualitative, non-numeric badge, present ONLY on a
   * small board (`rank` is `null` in that case). Never present on a
   * numerically-ranked board. Tercile-based placeholder, not a trend badge —
   * see `docs/API_BACKEND.md` §9.6. Optional (not just nullable) so a
   * pre-PR-6 test fixture that never mentions it still type-checks.
   */
  badge?: LeaderboardSmallBoardBadge | null;
  /**
   * Goal Score follow-ups batch 1 (frontend). Populated only when the
   * board's `ranked_by === 'total'`; `null` on a Form-ranked board (J §1.3).
   * `form_effect` is the persisted multiplier of that same evaluation
   * (an `evaluations_index` column since this batch) — the frontend never
   * re-derives it. All three `null`
   * together means "no row breakdown" (J §1.3's sub-line is simply hidden),
   * never rendered as `0` (LFT-063). Optional so a pre-this-batch fixture
   * that omits them still type-checks.
   */
  goal_bonus_score?: number | null;
  form_effect?: number | null;
  total_score?: number | null;
}

export interface LeaderboardResponse {
  channel_slug: string;
  channel_name: string;
  template_id: string | null;
  template_name: string | null;
  leaderboard_enabled: boolean;
  entries: LeaderboardEntry[];
  total_opted_in: number;
  is_member: boolean;
  /**
   * Goal Score PR-6: which metric this board is actually ranked by.
   * `'form'` is the pre-PR-6, unchanged `overall_score` ranking (always the
   * value when `SPORTFORM_LEADERBOARD_RANK_TOTAL` is off — byte-identical to
   * before this field existed). `'total'` means the Total Score ranking is
   * active. Optional + defaults to `'form'` at the call site so an older
   * fixture/mock that omits this field still behaves like the pre-PR-6
   * board.
   */
  ranked_by?: LeaderboardRankedBy;
  /**
   * Set only when `ranked_by === 'form'` because a Total ranking was
   * requested/enabled but this board has no eligible published `total_score`
   * yet. `null`/absent when the flag is off (the "concept doesn't exist"
   * case) or when `ranked_by === 'total'`.
   */
  fallback_reason?: string | null;
  /**
   * J §5.3 board-scope caveat. Present whenever `template_id` is set, in
   * BOTH flag states — this is a deliberate, flag-independent addition (see
   * `channel_leaderboard_routes.py`'s own comment: "always present once a
   * specific template is in view, regardless of the flag"), unlike
   * `ranked_by`/`fallback_reason`/`board_size_mode`, which ARE frozen to
   * their pre-PR-6-equivalent values when the flag is off.
   */
  board_scope_disclosure?: string | null;
  /**
   * `'small_board'` when fewer than the small-board floor (default 8)
   * athletes are ranked — entries carry `badge` instead of `rank` in that
   * mode.
   */
  board_size_mode?: LeaderboardBoardSizeMode;
}

export interface LeaderboardOptInToggleResponse {
  channel_id: string;
  user_id: string;
  leaderboard_opt_in: boolean;
}

export interface ChannelLeaderboardToggleResponse {
  channel_id: string;
  leaderboard_enabled: boolean;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * Fetch the channel leaderboard.
 *
 * Members see all opted-in athletes; non-members of public channels see
 * only the top-3 teaser.
 */
export async function getChannelLeaderboard(
  slug: string,
  params?: {
    template_id?: string;
    limit?: number;
    /**
     * Goal Score go-live B2 — explicit viewer choice between the two board
     * metrics. Omit for the existing flag-driven default.
     */
    metric?: LeaderboardRankedBy;
    /**
     * Goal Score go-live B2 — reorder (not re-rank) the returned entries by
     * one column: 'rank' | 'personal_best' | 'session_count' |
     * 'last_eval_at' | 'display_name'. Omit to keep the ranked order.
     */
    sort?: string;
    /** 'asc' | 'desc'. Only used when `sort` is set. */
    order?: 'asc' | 'desc';
  },
): Promise<LeaderboardResponse> {
  const { data } = await apiClient.get<LeaderboardResponse>(
    `/channels/${slug}/leaderboard`,
    { params },
  );
  return data;
}

/**
 * Toggle the leaderboard_opt_in flag for a channel member.
 *
 * The member can toggle their own opt-in; the channel owner can toggle any member.
 */
export async function toggleLeaderboardOptIn(
  slug: string,
  userId: string,
): Promise<LeaderboardOptInToggleResponse> {
  const { data } = await apiClient.patch<LeaderboardOptInToggleResponse>(
    `/channels/${slug}/members/${userId}/leaderboard-opt-in`,
  );
  return data;
}

/**
 * Toggle leaderboard_enabled on the channel itself (owner only).
 */
export async function toggleChannelLeaderboard(
  slug: string,
): Promise<ChannelLeaderboardToggleResponse> {
  const { data } = await apiClient.patch<ChannelLeaderboardToggleResponse>(
    `/channels/${slug}/leaderboard`,
  );
  return data;
}
